import { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useLocation, useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, SESSION_EXPIRED_EVENT } from "@/lib/api";
import { t } from "@/lib/i18n";
import { AppShell } from "@/components/app-shell";
import { PageLoading } from "@/components/loading";
import { LoginPage } from "@/pages/login";
import { NotFoundPage } from "@/pages/not-found";
import { PublicApplicationPage } from "@/pages/public-application";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/password-reset";
import { redirectAfterLogin } from "@/lib/utils";
import type { Organization, User } from "@/types";

const DashboardPage = lazy(() => import("@/pages/dashboard").then((module) => ({ default: module.DashboardPage })));
const CandidatesPage = lazy(() => import("@/pages/candidates").then((module) => ({ default: module.CandidatesPage })));
const CandidatePage = lazy(() => import("@/pages/candidate-detail").then((module) => ({ default: module.CandidatePage })));
const KanbanPage = lazy(() => import("@/pages/kanban").then((module) => ({ default: module.KanbanPage })));
const HiringPage = lazy(() => import("@/pages/hiring").then((module) => ({ default: module.HiringPage })));
const TasksPage = lazy(() => import("@/pages/tasks").then((module) => ({ default: module.TasksPage })));
const InterviewsPage = lazy(() => import("@/pages/interviews").then((module) => ({ default: module.InterviewsPage })));
const DocumentsPage = lazy(() => import("@/pages/documents").then((module) => ({ default: module.DocumentsPage })));
const ReportsPage = lazy(() => import("@/pages/reports").then((module) => ({ default: module.ReportsPage })));
const ManagementPage = lazy(() => import("@/pages/management").then((module) => ({ default: module.ManagementPage })));

/** Every route used to leave the tab reading "DriverFlow CRM", so browser
 *  history, bookmarks and a row of open tabs were all indistinguishable. */
const titles: [RegExp, string][] = [
  [/^\/dashboard/, "nav_dashboard"],
  [/^\/hiring|^\/vacancies/, "nav_hiring"],
  [/^\/candidates\/\d+/, "candidates_th_candidate"],
  [/^\/candidates/, "nav_candidates"],
  [/^\/kanban/, "nav_hiring_pipeline"],
  [/^\/interviews/, "nav_interviews"],
  [/^\/tasks/, "nav_tasks"],
  [/^\/documents/, "nav_documents"],
  [/^\/reports/, "nav_reports"],
  [/^\/employees/, "nav_employees"],
  [/^\/settings/, "nav_settings"],
  [/^\/notifications/, "nav_notifications"],
  [/^\/audit/, "nav_audit_log"],
  [/^\/profile/, "profile_heading"],
  [/^\/login/, "btn_sign_in"],
  [/^\/forgot-password/, "forgot_heading"],
  [/^\/reset-password/, "reset_heading"],
  // The public form is the one page outsiders see — at least name it.
  [/^\/apply/, "spa_apply_now"],
];

function useDocumentTitle() {
  const [location] = useLocation();
  useEffect(() => {
    const key = titles.find(([pattern]) => pattern.test(location))?.[1];
    document.title = key ? `${t(key)} · ${t("app_title")}` : t("app_title");
  }, [location]);
}

export function App() {
  // A trailing slash (common when links travel through messengers) used to miss
  // the public route entirely and bounce drivers to /login.
  const publicMatch = window.location.pathname.match(/^\/apply\/(\d+)\/?$/);
  // The session query must not sit behind the early return — it would make the
  // hook count depend on the route. It simply does not run on the public page.
  const session = useQuery<{ user: User; organization: Organization | null }>({
    queryKey: ["session"],
    queryFn: () => api("/api/auth/me"),
    // A 401 means signed-out (go to login); a network hiccup deserves a retry.
    retry: (count, error) => {
      const status = error instanceof Error && "status" in error ? (error as { status: number }).status : 0;
      if (status >= 400 && status < 500) return false;
      return count < 1;
    },
    refetchOnWindowFocus: true,
    enabled: !publicMatch,
    staleTime: 60_000,
  });
  const queryClient = useQueryClient();
  const search = useSearch();
  useDocumentTitle();

  // One place reacts to an expired session: keep the deep link in ?redirect=
  // and hand over to sign-in instead of letting every mutation fail alone.
  useEffect(() => {
    if (publicMatch) return;
    let redirected = false;
    const onExpired = () => {
      if (redirected) return;
      redirected = true;
      queryClient.setQueryData(["session"], null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
      const here = `${window.location.pathname}${window.location.search}`;
      window.location.replace(`/login?redirect=${encodeURIComponent(here)}`);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [queryClient, publicMatch]);

  if (publicMatch) return <PublicApplicationPage vacancyId={Number(publicMatch[1])} />;
  const isUnauthorized = (session.error instanceof ApiError && session.error.status === 401) ||
    (Boolean(session.error) && "status" in (session.error as { status?: number }) && (session.error as { status?: number }).status === 401);
  // A network blip during /api/auth/me is not a logout — show a retry screen
  // instead of silently presenting the sign-in page over a live session.
  if (session.isError && !session.data && !isUnauthorized) return <div className="p-8">
    <div className="empty-state" style={{ minHeight: "50vh" }}>
      <strong>{t("spa_failed_to_load_data")}</strong>
      <span>{t("spa_request_failed")}</span>
      <button className="secondary-button" type="button" onClick={() => session.refetch()}>{t("spa_retry")}</button>
    </div>
  </div>;
  if (session.isLoading || session.isFetching && !session.data) return <div className="p-8"><PageLoading /></div>;
  // Password recovery is reachable without a session: the whole point is that
  // the visitor cannot sign in.
  if (!session.data) return <Switch>
    <Route path="/login" component={LoginPage} />
    <Route path="/forgot-password" component={ForgotPasswordPage} />
    <Route path="/reset-password/:token">{(params) => <ResetPasswordPage token={String(params.token)} />}</Route>
    <Route><Redirect to="/login" /></Route>
  </Switch>;

  return <AppShell user={session.data.user} organization={session.data.organization}><Suspense fallback={<PageLoading />}><Switch>
    <Route path="/dashboard" component={DashboardPage} />
    {/* Already signed in: the sign-in link is still in browser history and in
        old bookmarks, and used to land on the 404 page instead of the app. */}
    <Route path="/login"><Redirect to={redirectAfterLogin(search)} /></Route>
    <Route path="/forgot-password"><Redirect to="/profile" /></Route>
    {/* The multi-posting model collapsed into one hiring profile; the old paths
        keep working so existing links and bookmarks do not break. */}
    <Route path="/hiring" component={HiringPage} />
    <Route path="/vacancies/:id"><Redirect to="/hiring" /></Route>
    <Route path="/vacancies"><Redirect to="/hiring" /></Route>
    {/* Garbage ids must hit the 404 page, not fire /api/candidates/NaN. */}
    <Route path="/candidates/:id">{(params) => /^\d+$/.test(String(params.id)) ? <CandidatePage id={Number(params.id)} /> : <NotFoundPage />}</Route><Route path="/candidates" component={CandidatesPage} />
    <Route path="/kanban" component={KanbanPage} /><Route path="/interviews" component={InterviewsPage} /><Route path="/tasks" component={TasksPage} />
    <Route path="/documents" component={DocumentsPage} /><Route path="/reports" component={ReportsPage} />
    <Route path="/employees">{() => <ManagementPage kind="employees" />}</Route>
    <Route path="/settings">{() => <ManagementPage kind="settings" />}</Route><Route path="/notifications">{() => <ManagementPage kind="notifications" />}</Route>
    <Route path="/audit">{() => <ManagementPage kind="audit" />}</Route><Route path="/profile">{() => <ManagementPage kind="profile" />}</Route>
    <Route path="/"><Redirect to="/dashboard" /></Route>
    <Route component={NotFoundPage} />
  </Switch></Suspense></AppShell>;
}
